import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CandidatePricePlan } from "../types/candidate/candidatePricePlan";
import { CompanyPricePlan } from "../types/company/companyPricePlan";
import { api } from "../services/api.service";

interface PricePlan {
  candidatePlans: CandidatePricePlan[];
  companyPlans: CompanyPricePlan[];
  candidateLoading: boolean;
  companyLoading: boolean;
  candidateError: string | null;
  companyError: string | null;
}

const initialState: PricePlan = {
  candidatePlans: [],
  companyPlans: [],
  candidateLoading: false,
  companyLoading: false,
  candidateError: null,
  companyError: null,
};

// Fixed function and thunk name
export const fetchCandidatePlans = createAsyncThunk<
  CandidatePricePlan[],
  { currency?: string } | undefined
>(
  "pricePlan/fetchCandidatePlans",
  async (params) => {
    const data = await api.pricePlan.candidate.getAll(params);
    return data;
  }
);

export const fetchCompanyPlans = createAsyncThunk<
  CompanyPricePlan[],
  { currency?: string } | undefined
>(
  "pricePlan/fetchCompanyPlans",
  async (params) => {
    const data = await api.pricePlan.company.getAll(params);
    return data;
  }
);

export const pricePlanSlice = createSlice({
  name: "pricePlan",
  initialState,
  reducers: {
    addCompanyPlan: (state, action: PayloadAction<CompanyPricePlan>) => {
      state.companyPlans.push(action.payload);
    },
    removeCompanyPlan: (state, action: PayloadAction<CompanyPricePlan>) => {
      state.companyPlans = state.companyPlans.filter(
        (plan) => plan.id !== action.payload.id
      );
    },
    updateCompanyPlan: (state, action: PayloadAction<CompanyPricePlan>) => {
      state.companyPlans = state.companyPlans.map((plan) =>
        plan.id === action.payload.id ? action.payload : plan
      );
    },
    addCandidatePlan: (state, action: PayloadAction<CandidatePricePlan>) => {
      state.candidatePlans.push(action.payload);
    },
    removeCandidatePlan: (state, action: PayloadAction<CandidatePricePlan>) => {
      state.candidatePlans = state.candidatePlans.filter(
        (plan) => plan.id !== action.payload.id
      );
    },
    updateCandidatePlan: (state, action: PayloadAction<CandidatePricePlan>) => {
      state.candidatePlans = state.candidatePlans.map((plan) =>
        plan.id === action.payload.id ? action.payload : plan
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // Candidate
      .addCase(fetchCandidatePlans.pending, (state) => {
        state.candidateLoading = true;
        state.candidateError = null;
      })
      .addCase(fetchCandidatePlans.fulfilled, (state, action) => {
        state.candidatePlans = action.payload;
        state.candidateLoading = false;
      })
      .addCase(fetchCandidatePlans.rejected, (state, action) => {
        state.candidateError = action.error.message || "Something went wrong";
        state.candidateLoading = false;
      })

      // Company
      .addCase(fetchCompanyPlans.pending, (state) => {
        state.companyLoading = true;
        state.companyError = null;
      })
      .addCase(fetchCompanyPlans.fulfilled, (state, action) => {
        state.companyPlans = action.payload;
        state.companyLoading = false;
      })
      .addCase(fetchCompanyPlans.rejected, (state, action) => {
        state.companyError = action.error.message || "Something went wrong";
        state.companyLoading = false;
      });
  },
});

export const {
  addCompanyPlan,
  removeCompanyPlan,
  updateCompanyPlan,
  addCandidatePlan,
  removeCandidatePlan,
  updateCandidatePlan,
} = pricePlanSlice.actions;

export default pricePlanSlice.reducer;
